import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useUserPreference } from "./user-preference-context";
import { getSessionData } from "../../utils/interceptors/localStorageService";

export const DASHBOARD_LAYOUTS = {
  DEFAULT: "default",
  MODERN_GRADIENT: "modern-gradient",
  GLASSMORPHISM: "glassmorphism",
  MINIMAL: "minimal",
};

const PREFERENCE_KEY = "dashboardLayout";
const LOCAL_STORAGE_KEY = "bl_dashboard_layout";
const BALANCE_PREF_KEY = "balanceVisible";
const BALANCE_LS_KEY = "bl_balance_visible";

const DashboardLayoutContext = createContext();

export const DashboardLayoutProvider = ({ children }) => {
  const [layout, setLayoutState] = useState(() => {
    return localStorage.getItem(LOCAL_STORAGE_KEY) || DASHBOARD_LAYOUTS.MINIMAL;
  });

  // true immediately if localStorage has a cached value (returning user — no flash)
  // false if cache is empty (fresh login) — wait for server fetch before rendering
  const [prefsLoaded, setPrefsLoaded] = useState(
    () => !!localStorage.getItem(LOCAL_STORAGE_KEY)
  );

  const [balanceVisible, setBalanceVisible] = useState(() => {
    const stored = localStorage.getItem(BALANCE_LS_KEY);
    return stored !== null ? stored === "true" : true;
  });

  const { updateUserPreference, getUserPreferences } = useUserPreference();

  // On mount: if the user is already authenticated, pull their stored preferences
  // from the server so the UI is correct even on a fresh device or after clearing cache.
  useEffect(() => {
    if (!getSessionData()) return;
    getUserPreferences([PREFERENCE_KEY, BALANCE_PREF_KEY]).then((prefs) => {
      if (!prefs || Object.keys(prefs).length === 0) return;

      const serverLayout = prefs[PREFERENCE_KEY];
      if (serverLayout && Object.values(DASHBOARD_LAYOUTS).includes(serverLayout)) {
        setLayoutState(serverLayout);
        localStorage.setItem(LOCAL_STORAGE_KEY, serverLayout);
      }

      const serverBalance = prefs[BALANCE_PREF_KEY];
      if (serverBalance !== undefined && serverBalance !== null) {
        const visible = serverBalance === true || serverBalance === "true";
        setBalanceVisible(visible);
        localStorage.setItem(BALANCE_LS_KEY, String(visible));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLayout = useCallback(
    (newLayout) => {
      if (!Object.values(DASHBOARD_LAYOUTS).includes(newLayout)) return;
      setLayoutState(newLayout);
      localStorage.setItem(LOCAL_STORAGE_KEY, newLayout);
      updateUserPreference(PREFERENCE_KEY, newLayout);
    },
    [updateUserPreference],
  );

  const reloadPreferences = useCallback(() => {
    if (!getSessionData()) {
      setPrefsLoaded(true);
      return;
    }
    getUserPreferences([PREFERENCE_KEY, BALANCE_PREF_KEY]).then((prefs) => {
      if (prefs && Object.keys(prefs).length > 0) {
        const serverLayout = prefs[PREFERENCE_KEY];
        if (serverLayout && Object.values(DASHBOARD_LAYOUTS).includes(serverLayout)) {
          setLayoutState(serverLayout);
          localStorage.setItem(LOCAL_STORAGE_KEY, serverLayout);
        }

        const serverBalance = prefs[BALANCE_PREF_KEY];
        if (serverBalance !== undefined && serverBalance !== null) {
          const visible = serverBalance === true || serverBalance === "true";
          setBalanceVisible(visible);
          localStorage.setItem(BALANCE_LS_KEY, String(visible));
        }
      }
      setPrefsLoaded(true);
    }).catch(() => setPrefsLoaded(true));
  }, [getUserPreferences]);

  const toggleBalanceVisible = useCallback(() => {
    setBalanceVisible((v) => {
      const next = !v;
      localStorage.setItem(BALANCE_LS_KEY, String(next));
      updateUserPreference(BALANCE_PREF_KEY, next);
      return next;
    });
  }, [updateUserPreference]);

  return (
    <DashboardLayoutContext.Provider value={{ layout, changeLayout, DASHBOARD_LAYOUTS, balanceVisible, toggleBalanceVisible, reloadPreferences, prefsLoaded }}>
      {children}
    </DashboardLayoutContext.Provider>
  );
};

export const useDashboardLayout = () => useContext(DashboardLayoutContext);
